import { ProjectSize, QueueProject } from "./projectType";
import { ScenarioProject } from "./scenarioResultType";
import { MisoPercentageReduction } from "./scenarioType";

export const reduceProjectSizeByPercentage = (
    projectSize: ProjectSize,
    percentageReduction: MisoPercentageReduction
): ProjectSize => {
    return {
        erisMw: reduceSizeByPercentage(
            projectSize.erisMw,
            percentageReduction.eris
        ),
        nrisMw: reduceSizeByPercentage(
            projectSize.nrisMw,
            percentageReduction.nris
        )
    };
};

// Hybrid Limit is only used in SPP so far — we want to cap the ER/NR MWs
// displayed to be the lower of Hybrid Limit / corresponding service type MW.
// So if a project in SPP has 40 MW ERIS, 0 MW NRIS, and 30 MW Hybrid Limit
// -> ERIS = 30, NRIS = 0 and Hybrid Limit = 30
const capToHybridLimit = (
    value: number,
    hybridLimitStr: string | undefined | null
): number => {
    if (hybridLimitStr && +hybridLimitStr !== 0) {
        return Math.min(+hybridLimitStr, value);
    }
    return value;
};

export const applyHybridLimitToProject = (
    project: QueueProject
): ProjectSize => {
    const hybridLimitStr = project.metadata?.["Hybrid Limit"];
    return {
        erisMw: capToHybridLimit(project.size.erisMw, hybridLimitStr),
        nrisMw: capToHybridLimit(project.size.nrisMw, hybridLimitStr)
    };
};

const reduceSizeByPercentage = (
    size: number,
    percentageReduction: number
): number => {
    return parseFloat((size - (percentageReduction / 100) * size).toFixed(2));
};

// Scale a project's Hybrid Limit by a scenario's ERIS reduction %.
//
// This mirrors what the backend does for cost allocation: in SPP's
// cost-allocation pipeline (see `iq_spp/service/cost_allocation/build_projects_cost_allocation.py::_enriched_requests`),
// `project_mw_size` is seeded from the project's Hybrid Limit for hybrid
// projects and then scaled by `assumption.eris` (the ERIS reduction %).
// The resulting `mw_impact = project_mw_size × dfax` on every constraint
// therefore uses `hybrid_limit × (1 − eris_reduction)`, not the static POI
// cap. See the code comment in `to_scenario_results.py`:
//   "We use project_mw_size (which accounts for hybrid limits) rather than
//    eris_capacity_mw (nameplate) so that hybrid projects reflect their
//    actual POI injection, matching what SPP dispatches in the study models."
//
// Using the ERIS reduction (not NRIS) keeps this helper consistent with the
// backend's `is_nris=False` call, even when the scenario sets asymmetric
// ERIS/NRIS reductions.
export const reduceHybridLimitByErisPercentage = (
    hybridLimit: number,
    percentageReduction: MisoPercentageReduction
): number => {
    return reduceSizeByPercentage(hybridLimit, percentageReduction.eris);
};

export const getHybridLimit = (
    scenarioProject: ScenarioProject,
    projectMetadata: { [key: string]: string } | null,
    useEris = false
): number => {
    const baseValue = useEris
        ? scenarioProject.erisSizeMw
        : scenarioProject.nrisSizeMw;
    return capToHybridLimit(baseValue, projectMetadata?.["Hybrid Limit"]);
};

/**
 * Returns true when a scenario assumption represents a fully-excluded
 * project — i.e. both ERIS and NRIS are reduced by 100%. This is the
 * canonical "the user dropped this project from the scenario" predicate
 * across MISO / NYISO / PJM / SPP. NYISO only ever produces this shape
 * because EXCLUSION is its only available sensitivity. MISO / PJM / SPP
 * also support partial downsizes and asymmetric reductions (e.g.
 * `nris === 100` only), which should NOT be treated as dropped because
 * the project still contributes to ERIS-thermal cost allocation.
 *
 * Accepts undefined / null so callers can pass `assumptions[projectId]`
 * directly without first checking presence.
 */
export const isProjectExcluded = (
    assumption: MisoPercentageReduction | undefined | null
): boolean => assumption?.eris === 100 && assumption?.nris === 100;
